import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../../components/MainLayout';
import {
  Building2,
  Info,
  Package,
  Users,
  Shield,
  User as UserIcon,
  Car,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@werkstatt/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import { UserDialog } from '../users/UserDialog';

interface MyTenantData {
  id: number;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  tax_id: string | null;
  vat_id: string | null;
  is_active: boolean;
  subscription_plan: string;
  subscription_status: string;
  max_admins: number;
  max_users: number;
  max_vehicles: number;
  max_customers: number;
  max_bookings_per_month: number;
  admins_count: number;
  users_count: number;
  vehicles_count: number;
  customers_count: number;
}

interface UserItem {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: string;
  is_active: boolean;
  membership_role: string;
  created_at: string;
}

interface UsersResponse {
  users: UserItem[];
  total: number;
  admins_count: number;
  users_count: number;
  vehicles_count: number;
  customers_count: number;
  max_admins: number;
  max_users: number;
  max_vehicles: number;
  max_customers: number;
}

const PLAN_LABELS: Record<string, string> = {
  basic: 'Basic',
  pro: 'Pro',
  ultra: 'Ultra',
  starter: 'Starter',
  business: 'Business',
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  active: { label: 'Aktiv', color: 'text-green-600' },
  cancelled: { label: 'Gekündigt', color: 'text-red-600' },
  suspended: { label: 'Gesperrt', color: 'text-orange-600' },
  trial: { label: 'Testphase', color: 'text-blue-600' },
};

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="font-medium mt-0.5">{value}</p>
    </div>
  );
}

function LimitBar({
  label,
  used,
  max,
  icon: Icon,
}: {
  label: string;
  used: number;
  max: number;
  icon: React.ComponentType<{ className?: string }>;
}) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  const color = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-400' : 'bg-primary';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </div>
        <span className={`font-medium tabular-nums ${pct >= 100 ? 'text-red-600' : ''}`}>
          {used} / {max}
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function MyTenant() {
  const [activeTab, setActiveTab] = useState('info');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: tenant, isLoading: tenantLoading } = useQuery<MyTenantData>({
    queryKey: ['my-tenant'],
    queryFn: async () => {
      const res = await api.get('/settings/my-tenant');
      return res.data;
    },
  });

  const { data: usersData, isLoading: usersLoading } = useQuery<UsersResponse>({
    queryKey: ['users', ''],
    queryFn: async () => {
      const res = await api.get('/users');
      return res.data;
    },
    enabled: activeTab === 'users',
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['my-tenant'] });
      toast({ title: 'Benutzer entfernt' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Benutzer konnte nicht entfernt werden',
      });
    },
  });

  const handleOpenCreate = () => {
    setEditingUser(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (user: UserItem) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const handleDelete = (user: UserItem) => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
    if (window.confirm(`Benutzer "${name}" aus diesem Mandanten entfernen?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  if (tenantLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Building2 className="h-7 w-7" />
            Mein Mandant
          </h1>
          {tenant && (
            <p className="text-muted-foreground mt-1">{tenant.name}</p>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto">
            <TabsList className="inline-flex min-w-full sm:max-w-lg">
              <TabsTrigger value="info" className="flex-1 flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4">
                <Info className="h-3.5 w-3.5 shrink-0" />
                Informationen
              </TabsTrigger>
              <TabsTrigger value="subscription" className="flex-1 flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4">
                <Package className="h-3.5 w-3.5 shrink-0" />
                Abo &amp; Limits
              </TabsTrigger>
              <TabsTrigger value="users" className="flex-1 flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4">
                <Users className="h-3.5 w-3.5 shrink-0" />
                Benutzer
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ── Tab: Informationen ─────────────────────────────── */}
          <TabsContent value="info" className="mt-6">
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="font-display">Mandanten-Informationen</CardTitle>
                <CardDescription>Stammdaten Ihres Mandanten</CardDescription>
              </CardHeader>
              <CardContent>
                {tenant && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    <InfoRow label="Name" value={tenant.name} />
                    <InfoRow label="Slug" value={<span className="font-mono text-sm">{tenant.slug}</span>} />
                    <InfoRow label="E-Mail" value={tenant.email} />
                    <InfoRow label="Telefon" value={tenant.phone} />
                    <InfoRow
                      label="Adresse"
                      value={
                        [tenant.street, [tenant.postal_code, tenant.city].filter(Boolean).join(' ')]
                          .filter(Boolean)
                          .join(', ') || null
                      }
                    />
                    <InfoRow label="Land" value={tenant.country} />
                    <InfoRow label="Steuernummer" value={tenant.tax_id} />
                    <InfoRow label="USt-IdNr." value={tenant.vat_id} />
                    <InfoRow
                      label="Status"
                      value={
                        <span className={tenant.is_active ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                          {tenant.is_active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      }
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Tab: Abo & Limits ──────────────────────────────── */}
          <TabsContent value="subscription" className="mt-6">
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="font-display">Abonnement &amp; Nutzungslimits</CardTitle>
                <CardDescription>
                  Ihr aktueller Plan und die Auslastung der enthaltenen Ressourcen
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {tenant && (
                  <>
                    {/* Plan badge */}
                    <div className="flex items-center gap-3 pb-4 border-b">
                      <div>
                        <p className="text-sm text-muted-foreground">Plan</p>
                        <p className="font-semibold text-lg mt-0.5">
                          {PLAN_LABELS[tenant.subscription_plan] ?? tenant.subscription_plan}
                        </p>
                      </div>
                      <div className="ml-auto">
                        <span
                          className={`px-3 py-1 rounded-pill text-sm font-medium ${
                            tenant.subscription_status === 'active'
                              ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {STATUS_LABELS[tenant.subscription_status]?.label ?? tenant.subscription_status}
                        </span>
                      </div>
                    </div>

                    {/* Limit bars */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                      <LimitBar label="Admins" used={tenant.admins_count} max={tenant.max_admins} icon={Shield} />
                      <LimitBar label="Benutzer" used={tenant.users_count} max={tenant.max_users} icon={UserIcon} />
                      <LimitBar label="Fahrzeuge" used={tenant.vehicles_count} max={tenant.max_vehicles} icon={Car} />
                      <LimitBar label="Kunden" used={tenant.customers_count} max={tenant.max_customers} icon={Users} />
                    </div>

                    <p className="text-xs text-muted-foreground pt-2">
                      Limits werden automatisch aktualisiert, wenn Ihr Abo im Portal geändert wird.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Tab: Benutzer ──────────────────────────────────── */}
          <TabsContent value="users" className="mt-6">
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="font-display flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span>
                    Benutzer
                    {usersData ? ` (${usersData.total})` : ''}
                  </span>
                  <Button onClick={handleOpenCreate} className="self-start sm:self-auto">
                    <Plus className="w-4 h-4 mr-2" />
                    Neuer Benutzer
                  </Button>
                </CardTitle>
                <CardDescription>
                  Benutzer und Rollen in diesem Mandanten
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {usersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                  </div>
                ) : !usersData || usersData.users.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">Noch keine Benutzer vorhanden</p>
                    <Button variant="outline" onClick={handleOpenCreate}>
                      <Plus className="w-4 h-4 mr-2" />
                      Ersten Benutzer anlegen
                    </Button>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                          Benutzer
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden sm:table-cell">
                          Rolle
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden sm:table-cell">
                          Status
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                          Aktionen
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {usersData.users.map((u) => {
                        const displayName =
                          [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
                        return (
                          <tr key={u.id} className="hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {u.membership_role === 'admin' ? (
                                  <Shield className="h-4 w-4 text-primary flex-shrink-0" />
                                ) : (
                                  <UserIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                )}
                                <div>
                                  <div className="font-medium text-sm">{displayName}</div>
                                  <div className="text-xs text-muted-foreground">{u.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span
                                className={`px-2 py-0.5 rounded-pill text-xs font-medium ${
                                  u.membership_role === 'admin'
                                    ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
                                    : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                {u.membership_role === 'admin' ? 'Admin' : 'Benutzer'}
                              </span>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span
                                className={`px-2 py-0.5 rounded-pill text-xs font-medium ${
                                  u.is_active
                                    ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                                    : 'bg-red-500/15 text-red-600 dark:text-red-400'
                                }`}
                              >
                                {u.is_active ? 'Aktiv' : 'Inaktiv'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenEdit(u)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(u)}
                                disabled={deleteMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <UserDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: ['my-tenant'] });
          }
        }}
        user={editingUser}
      />
    </MainLayout>
  );
}
